import { inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { AuthService } from '@fluxonos/features/user/services/auth.service';
import { TokenDto, IOperatorStatusPayload, SocketEventEnum } from '@fluxonos/fluxonos-dto';
import { environment } from '@fluxonos/environments/environment';
import { toObservable } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root'
})
export class SocketEventService {
  private readonly _authService = inject(AuthService);

  private _subscription: Subscription | undefined = undefined;
  private _socket: Socket | undefined;
  private _connect$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private _data = signal<any | null>(null);
  private _data$ = toObservable(this._data);

  private _operatorStatus$ = new BehaviorSubject<IOperatorStatusPayload | null>(null);
  private _operatorHeartbeat$ = new BehaviorSubject<IOperatorStatusPayload | null>(null);

  private _initWs(): void {
    if (this._socket) {
      console.warn('Socket instance is already init!');
      return;
    }
    this._socket = io(`${environment.apiUrl}/events`, {
      auth: {},
      extraHeaders: {},
      autoConnect: false,
      reconnection: true,
      reconnectionDelay: 10000,
      transports: ['websocket' /*'polling'*/],
      secure: true
    });

    this._socket.on(SocketEventEnum.CONNECT, () => {
      console.log(`events ws [${SocketEventEnum.CONNECT}]`);
      this._connect$.next(true);
      return;
    });

    this._socket.on(SocketEventEnum.DISCONNECT, (data) => {
      console.log(`events ws [${SocketEventEnum.DISCONNECT}]`);
      this._connect$.next(false);
      setTimeout(() => {
        this._connectWs(this._authService.token);
      }, 2000);
    });

    this._socket.on(SocketEventEnum.RECONNECT, (data) => {
      console.log(SocketEventEnum.RECONNECT, data);
    });

    this._socket.on('operator/status', (data: IOperatorStatusPayload) => {
      if (data) this._operatorStatus$.next(data);
    });

    this._socket.on('operator/heartbeat', (data: IOperatorStatusPayload) => {
      if (data) this._operatorHeartbeat$.next(data);
    });
  }

  //
  private _connectWs(authToken: TokenDto | null): void {
    if (!this._socket) {
      return;
    }
    if (!authToken) {
      (this._socket as any).auth = null;
      if (!!this._socket?.disconnect) {
        this._socket?.disconnect();
      }
      return;
    }

    // ${authToken.tokenType}
    (this._socket as any).auth = {
      authorization: `bearer ${authToken.accessToken}`
    };
    if (!!this._socket?.connect) {
      this._socket?.connect();
    }
  }

  public initialize(): void {
    this.connect();

    // this._socketEventService.operatorStatus$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
    //   next: (data) => {
    //     console.log('operator/status', data);
    //   }
    // });
    // this._socketEventService.operatorHeartbeat$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
    //   next: (data) => {
    //     console.log(data);
    //   }
    // });
  }

  public connect(): void {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
    this._subscription = new Subscription();

    this._initWs();

    this._subscription.add(
      this._authService.token$.subscribe({
        next: (data: TokenDto | null) => {
          this._connectWs(data);
        }
      })
    );
  }

  public disconnect(): void {
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = undefined;
    }
    if (this._socket) {
      this._socket.close();
      this._socket.disconnect();
      this._socket = undefined;
    }
    this._connect$.next(false);
  }

  public get data$(): Observable<any> {
    return this._data$;
  }

  public get operatorStatus$(): Observable<IOperatorStatusPayload | null> {
    return this._operatorStatus$.asObservable();
  }

  public get operatorHeartbeat$(): Observable<IOperatorStatusPayload | null> {
    return this._operatorHeartbeat$.asObservable();
  }
}
